import _ from 'underscore';
import type {
  EntityKey, EntityObject, OverreactRequestShape, StoredEntity,
} from '../public-types';
import { Subject } from '../utils/observer-pattern';
import { OVERREACT_ID_FIELD_NAME } from './consts';
import { Record } from './record';
import type { Store } from './store';

interface RecordGroupOptions<TEntity extends EntityObject> {
  schemaType: string;
  store: Store<TEntity>;
}

export class RecordGroup<TEntity extends EntityObject = EntityObject> extends Subject {
  records: Array<Record<TEntity>>;
  schemaType: string;
  store: Store<TEntity>;

  constructor({ schemaType, store }: RecordGroupOptions<TEntity>) {
    super();
    this.schemaType = schemaType;
    this.store = store;
    this.records = [];

    this.addOrUpdateRecords = this.addOrUpdateRecords.bind(this);
    this.getRecords = this.getRecords.bind(this);
    this.getRecordsByEntityKeys = this.getRecordsByEntityKeys.bind(this);
  }

  findIndex(dataId: string) {
    return this.records.findIndex(record => record.id === dataId);
  }

  addOrUpdateRecordInternal(data: StoredEntity<TEntity>) {
    const dataId = data[OVERREACT_ID_FIELD_NAME];
    const recordIndex = this.findIndex(dataId);
    if (recordIndex > -1) {
      this.records[recordIndex].setData(data.rawData);
    } else {
      this.records.push(new Record(dataId, this.schemaType, data.rawData));
    }
    return dataId;
  }

  addOrUpdateRecords(
    dataItems: Array<StoredEntity<TEntity>>,
    request?: OverreactRequestShape,
  ) {
    const updatedDataIDs = dataItems.map(data => this.addOrUpdateRecordInternal(data));
    this.notify('dataRefIdsUpdate', updatedDataIDs, request);
    this.notifyUpdate();
  }

  deleteRecords(ids: string[]) {
    this.records = this.records.filter(record => !_.contains(ids, record.id));
    this.notifyUpdate();
  }

  getRecords(ids: string[]) {
    return ids.map(id => this.records.find(record => record.id === id));
  }

  getRecordsByEntityKeys(keySelector: (_entity: TEntity) => EntityKey, keys: EntityKey[]) {
    return _.chain(keys)
      .map(key => this.records.find(record => keySelector(record.getData()) === key))
      .compact()
      .value() as Array<Record<TEntity>>;
  }

  notifyUpdate() {
    if (window.__OVERREACT_DEVTOOLS__) {
      window.__OVERREACT_DEVTOOLS__.onRecordGroupChange({
        storeId: this.store.uniqueId,
        schemaType: this.schemaType,
        records: JSON.parse(JSON.stringify(this.records)),
      });
    }
  }
}
